/**
 * Sort primitives used by the grid's internal sort state (click-to-sort
 * on a column header) and the `defaultSort` multi-level ordering.
 *
 * Kept outside `DataGrid.tsx` so the body JSX doesn't drown in helper
 * functions, and so tests can exercise them in isolation.
 */

import type { DataGridColumn } from './types';
import { readAccessor } from './cell-content';

/**
 * Read the comparable value for a column on a given row. Priority :
 *   `sortValue` → `accessor` (key or function) → `undefined`.
 *
 * `undefined` is a signal to the comparator to push the row to the end
 * regardless of direction.
 */
export function readSortValue<T>(
  column: DataGridColumn<T>,
  row: T
): string | number | Date | null | undefined {
  if (column.sortValue) return column.sortValue(row);
  if (column.accessor === undefined) return undefined;
  return readAccessor(column.accessor, row);
}

/**
 * Generic comparator that handles nullish, strings, numbers and Date
 * objects. Null/undefined always sort last regardless of direction —
 * empty cells climbing to the top of an ascending sort is confusing.
 */
export function compareValues(
  a: string | number | Date | null | undefined,
  b: string | number | Date | null | undefined,
  direction: 'asc' | 'desc'
): number {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  let cmp = 0;
  if (a instanceof Date && b instanceof Date) {
    cmp = a.getTime() - b.getTime();
  } else if (typeof a === 'number' && typeof b === 'number') {
    cmp = a - b;
  } else {
    cmp = String(a).localeCompare(String(b), 'fr', {
      sensitivity: 'base',
      numeric: true,
    });
  }

  return direction === 'asc' ? cmp : -cmp;
}
